import { Injectable } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';
import {
  RequerimentoAdminDetalheDto,
  RequerimentoAdminDocumentoDto,
  RequerimentoAdminHistoricoDto,
  RequerimentoAdminItemDto,
  RequerimentoAdminQueryParams,
} from '../api/models';
import {
  RequerimentoOnlineDetalhe,
  RequerimentoOnlineDocumento,
  RequerimentoOnlineFiltros,
  RequerimentoOnlineHistorico,
  RequerimentoOnlineOpcoesFiltro,
  RequerimentoOnlineReenvioItem,
  RequerimentoOnlineSolicitacao,
} from '../models/requerimento-beneficio/requerimento-online-solicitacao.model';
import { extractIsoDate } from '../utils/date-format.util';
import { BeneficiosApiService } from './parametros/beneficios-api.service';
import { PlanosApiService } from './parametros/planos-api.service';
import { RequerimentoOnlineSolicitacaoApiService } from './requerimento-online-solicitacao-api.service';

/** Planos parametrizados cabem numa página: a API de Parametrização aceita até 100 por página. */
const PLANOS_POR_PAGINA = 100;

/**
 * Solicitações do Requerimento Online para a tela: traduz os filtros para a API e o retorno para o model. As chamadas
 * HTTP ficam no `RequerimentoOnlineSolicitacaoApiService`. As ações do Visualizar devolvem o detalhe atualizado.
 */
@Injectable({
  providedIn: 'root',
})
export class RequerimentoOnlineSolicitacaoService {
  constructor(
    private readonly requerimentoApiService: RequerimentoOnlineSolicitacaoApiService,
    private readonly planosApiService: PlanosApiService,
    private readonly beneficiosApiService: BeneficiosApiService,
  ) {}

  /** A API filtra e devolve a lista inteira; ordenação e paginação ficam na tela. */
  listarSolicitacoes(filtros?: RequerimentoOnlineFiltros): Observable<RequerimentoOnlineSolicitacao[]> {
    return this.requerimentoApiService
      .listarRequerimentos(this.toQueryParams(filtros))
      .pipe(map((lista) => lista.map((dto) => this.mapSolicitacao(dto))));
  }

  /** Planos e benefícios ativos da Parametrização e os status da API de Requerimento, para os combos. */
  listarOpcoesFiltro(): Observable<RequerimentoOnlineOpcoesFiltro> {
    return forkJoin({
      planos: this.planosApiService
        .listarPlanosParams({ ativo: 'S', pagina: 1, tamanho_pagina: PLANOS_POR_PAGINA })
        .pipe(
          map((resposta) =>
            resposta.itens
              .filter((plano) => !!plano.cod_plano)
              .map((plano) => ({ value: plano.cod_plano!, label: plano.ds_plano ?? plano.cod_plano! })),
          ),
        ),
      beneficios: this.beneficiosApiService
        .listarCatalogo({ ativo: 'S' })
        .pipe(map((lista) => lista.map((beneficio) => ({ value: String(beneficio.id), label: beneficio.nome_exibicao })))),
      status: this.requerimentoApiService
        .listarStatus()
        .pipe(map((lista) => lista.map((status) => ({ value: status.tp_status, label: status.ds_status })))),
    });
  }

  /** Detalhe pelo `idSolicitacao` da lista; rascunho ou id inexistente respondem 404. */
  obterSolicitacao(idSolicitacao: number | string): Observable<RequerimentoOnlineDetalhe> {
    return this.requerimentoApiService.obterRequerimento(Number(idSolicitacao)).pipe(map((dto) => this.mapDetalhe(dto)));
  }

  /** Arquivo do documento, para baixar ou visualizar (a API exige token e chave: não há link direto). */
  obterArquivo(idSolicitacao: number, idDocumento: number): Observable<Blob> {
    return this.requerimentoApiService.obterArquivo(idSolicitacao, idDocumento);
  }

  /** Aprova os documentos pendentes; a solicitação segue para a integração (em processamento ou erro). */
  aprovarDocumentos(idSolicitacao: number): Observable<RequerimentoOnlineDetalhe> {
    return this.requerimentoApiService.aprovarDocumentos(idSolicitacao).pipe(map((dto) => this.mapDetalhe(dto)));
  }

  /** Recusa os documentos marcados, cada um com o seu motivo; os demais pendentes são aprovados. */
  solicitarReenvio(idSolicitacao: number, itens: RequerimentoOnlineReenvioItem[]): Observable<RequerimentoOnlineDetalhe> {
    const documentos = itens.map((item) => ({ id_documento: item.idDocumento, ds_motivo_recusa: item.motivo.trim() }));
    return this.requerimentoApiService
      .solicitarReenvio(idSolicitacao, { documentos })
      .pipe(map((dto) => this.mapDetalhe(dto)));
  }

  /** "Aprovar solicitação": o operador conferiu no corporativo que o processo foi concluído. */
  concluirSolicitacao(idSolicitacao: number): Observable<RequerimentoOnlineDetalhe> {
    return this.requerimentoApiService.concluirSolicitacao(idSolicitacao).pipe(map((dto) => this.mapDetalhe(dto)));
  }

  /** "Registrar erro": o processo falhou no corporativo. */
  registrarErroIntegracao(idSolicitacao: number, motivo: string): Observable<RequerimentoOnlineDetalhe> {
    return this.requerimentoApiService
      .registrarErroIntegracao(idSolicitacao, { ds_motivo_erro_integracao: motivo.trim() })
      .pipe(map((dto) => this.mapDetalhe(dto)));
  }

  /** "Reprocessar": envia a solicitação de novo ao corporativo. */
  reprocessarIntegracao(idSolicitacao: number): Observable<RequerimentoOnlineDetalhe> {
    return this.requerimentoApiService.reprocessarIntegracao(idSolicitacao).pipe(map((dto) => this.mapDetalhe(dto)));
  }

  private toQueryParams(filtros: RequerimentoOnlineFiltros = {}): RequerimentoAdminQueryParams {
    return {
      nome: filtros.nome,
      matricula: filtros.matricula,
      cod_plano: filtros.codPlano,
      id_beneficio: filtros.idBeneficio,
      tp_status: filtros.tpStatus,
      dt_solicitacao_ini: filtros.periodoDe,
      dt_solicitacao_fim: filtros.periodoAte,
    };
  }

  private mapSolicitacao(dto: RequerimentoAdminItemDto): RequerimentoOnlineSolicitacao {
    return {
      idSolicitacao: dto.id,
      nomeParticipante: dto.nom_participante ?? '-',
      protocolo: dto.num_protocolo,
      matricula: dto.matricula,
      codPlano: dto.cod_plano,
      plano: dto.ds_plano ?? dto.cod_plano,
      idBeneficio: dto.id_beneficio,
      tipoBeneficio: dto.ds_beneficio ?? '-',
      dataSolicitacao: dto.dt_solicitacao ? extractIsoDate(dto.dt_solicitacao) : null,
      tpStatus: dto.tp_status,
      status: dto.ds_status,
    };
  }

  /** O detalhe mostra data e hora: as datas ficam com a hora da API. */
  private mapDetalhe(dto: RequerimentoAdminDetalheDto): RequerimentoOnlineDetalhe {
    return {
      ...this.mapSolicitacao(dto),
      dataSolicitacao: dto.dt_solicitacao,
      ultimaAtualizacao: dto.dt_ultima_atualizacao,
      dataCancelamento: dto.dt_cancelamento,
      motivoCancelamento: dto.ds_motivo_cancel,
      permiteAnalisarDocumentos: dto.permite_analisar_documentos,
      motivoBloqueioAnalise: dto.motivo_bloqueio_analise,
      motivoErroIntegracao: dto.ds_motivo_erro_integracao,
      permiteConcluirSolicitacao: dto.permite_concluir_solicitacao,
      permiteRegistrarErro: dto.permite_registrar_erro,
      permiteReprocessar: dto.permite_reprocessar,
      documentos: (dto.documentos ?? []).map((documento) => this.mapDocumento(documento)),
      historico: (dto.historico ?? []).map((item) => this.mapHistorico(item)),
    };
  }

  private mapDocumento(dto: RequerimentoAdminDocumentoDto): RequerimentoOnlineDocumento {
    return {
      idDocumento: dto.id,
      tipoDocumento: dto.ds_tipo_documento ?? 'Documento',
      nomeArquivo: dto.nom_arquivo,
      contentType: dto.ds_content_type,
      enviadoEm: dto.dt_upload,
      tpStatusAnalise: dto.tp_status_analise,
      motivoRecusa: dto.ds_motivo_recusa,
      analisadoEm: dto.dt_analise,
      reenvio: dto.fl_reenvio,
      recusaAnterior: dto.recusa_anterior
        ? { motivo: dto.recusa_anterior.ds_motivo_recusa, analisadoEm: dto.recusa_anterior.dt_analise }
        : null,
    };
  }

  private mapHistorico(dto: RequerimentoAdminHistoricoDto): RequerimentoOnlineHistorico {
    return {
      idHistorico: dto.id,
      statusAnterior: dto.ds_status_ant,
      statusNovo: dto.ds_status_novo,
      observacao: dto.ds_observacao,
      origem: dto.origem,
      alteradoEm: dto.dt_inclusao,
    };
  }
}
